import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {IconButton} from '@mui/material';
import {MdEdit, MdInfo, MdDelete} from 'react-icons/md';
import Swal from 'sweetalert2';
import Modal from '../../components/Modal';
import DateDisplay from '../../components/DateDisplay';
import StatusIcon from '../../components/StatusIcon';
import DataTable from '../../components/data_table';
import {useClientSubscriptions} from '../../processes/client_subscription_schedule';
import EditStatusDialog from './EditStatusDialog';
import NotificationDetailsDialog from './NotificationDetailsDialog';
import {useDeleteClientSubscription} from "../../processes/client_subscription_active";

export default function SubscriptionsDialog({ subscription, onClose, refetchAllClientSummaries }) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingSub, setEditingSub] = useState(null);
    const [viewingSub, setViewingSub] = useState(null);

    const clientId = subscription?.client?.id;
    const clientName = subscription?.client?.name;

    const { data: subs = [], isLoading, isFetching, isError, error, refetch } = useClientSubscriptions(clientId);
    const { mutate: deleteSubscription, isPending: isDeleting } = useDeleteClientSubscription();

    useEffect(() => { if (clientId) { setIsDialogOpen(true); refetch(); } }, [clientId, refetch]);

    const handleClose = useCallback(() => { setIsDialogOpen(false); onClose?.(); }, [onClose]);
    const handleRefresh = useCallback(() => { refetch(); }, [refetch]);
    const onEditSuccess = useCallback(() => { setEditingSub(null); refetchAllClientSummaries?.(); refetch(); }, [refetchAllClientSummaries, refetch]);

    const handleDelete = useCallback(async (row) => {
        const modalEl = document.querySelector('dialog[open]');


        const confirm = await Swal.fire({
            target: modalEl || document.body,
            icon: 'warning',
            title: 'Delete this subscription?',
            text: `Client: ${row?.original?.client?.name || clientName}`,
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
            reverseButtons: true,
            backdrop: true,
            heightAuto: false,
            returnFocus: false,
        });


        if (!confirm.isConfirmed) return;


        deleteSubscription(
            { subscriptionId: row.id },
            {
                onSuccess: (data) => {
                    Swal.fire({
                        target: modalEl || document.body,
                        icon: data?.ok ? 'success' : 'error',
                        title: data?.ok ? 'Deleted!' : 'Unexpected Response',
                        text: data?.ok ? 'Subscription deleted successfully.' : 'Please try again later.',
                        timer: data?.ok ? 2000 : undefined,
                        showConfirmButton: !data?.ok,
                        heightAuto: false,
                        returnFocus: false,
                    });
                    if (data?.ok) {
                        refetch();
                        refetchAllClientSummaries?.();
                    }
                },
                onError: (err) => {
                    Swal.fire({
                        target: modalEl || document.body,
                        icon: 'error',
                        title: 'Delete Failed',
                        text: err?.message || 'Unable to delete this subscription.',
                        heightAuto: false,
                        returnFocus: false,
                    });
                },
            }
        );
    }, [deleteSubscription, clientName, refetch, refetchAllClientSummaries]);

    const columns = useMemo(() => ([
        { id: 'id', header: 'ID', accessorKey: 'id' },
        { id: 'start', header: 'Start', accessorKey: 'start', size: 120, Cell: ({cell}) => <DateDisplay date={cell.getValue()} format="DD MMM YYYY" /> },
        { id: 'end', header: 'End', accessorKey: 'end', size: 120, Cell: ({cell}) => <DateDisplay date={cell.getValue()} format="DD MMM YYYY" /> },
        { id: 'reminders', header: 'Reminder Emails', accessorKey: 'reminders', size: 220, Cell: ({cell}) => {
                const val = cell.getValue();
                if (!val || (Array.isArray(val) && val.length === 0)) return <span className="tw-text-gray-400">-</span>;
                const text = Array.isArray(val) ? val.join(', ') : String(val);
                return <span className="tw-text-sm" title={text}>{text}</span>;
            }},
        { id: 'updatedAt', header: 'Last Updated', accessorKey: 'updatedAt', size: 170, Cell: ({cell}) => (<DateDisplay date={cell.getValue()} format="YYYY-MM-DD HH:mm:ss" />) },
        { id: 'isActive', header: 'Active', accessorKey: 'isActive', size: 80, Cell: ({cell}) => <StatusIcon status={cell.getValue()} /> },
        { id: 'actions', header: '', size: 120, Cell: ({row}) => {
                const hasId = !!row.id;
                return (
                    <div className="tw-flex tw-gap-1 tw-justify-end">
                        <IconButton size="small" title="Edit Subscription Status" onClick={() => hasId && setEditingSub(row)} disabled={!hasId}><MdEdit fontSize={'1.25rem'} /></IconButton>
                        <IconButton size="small" title="View Notification Details" onClick={() => hasId && setViewingSub(row)} disabled={!hasId}><MdInfo fontSize={'1.25rem'} /></IconButton>
                        <IconButton size="small" title="Delete Subscription" color="error" onClick={() => hasId && handleDelete(row)} disabled={!hasId}><MdDelete fontSize={'1.25rem'} /></IconButton>
                    </div>
                );
            }},
    ]), [handleDelete]);

    const emptyStateConfig = {
        title: 'No subscriptions found for this client.',
        subtitle: clientId ? `Client ID: ${clientId}` : '',
        actionButton: (
            <button className="tw-btn tw-btn-sm tw-btn-outline" onClick={handleRefresh} disabled={isLoading || isFetching}>Refresh</button>
        ),
        loadingMessage: 'Loading subscriptions...',
    };

    if (!subscription) return null;

    return (
        <Modal isOpen={isDialogOpen} onClose={handleClose} title={`Subscriptions – ${clientName || 'Unknown Client'}`} subtitle={clientId ? `Client ID: ${clientId}` : ''} size="tw-max-w-5xl">
            <div className="tw-flex tw-flex-col tw-h-full tw-max-h-[70vh]">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <div className="tw-text-sm tw-text-gray-500">{isFetching ? 'Refreshing…' : 'History of all subscription windows for this client.'}</div>
                    <div className="tw-flex tw-gap-2">
                        <button className="tw-btn tw-btn-sm tw-btn-outline" onClick={handleRefresh} disabled={isLoading || isFetching}>Refresh</button>
                        <button className="tw-btn tw-btn-sm" onClick={handleClose}>Close</button>
                    </div>
                </div>
                <div className="tw-flex-1 tw-min-h-0 tw-overflow-auto">
                    <DataTable data={subs} columns={columns} isLoading={isLoading || isFetching} error={isError ? error : null} onRetry={handleRefresh} emptyStateConfig={emptyStateConfig} tableClassName="tw-w-full" />
                </div>
            </div>

            {editingSub && (
                <EditStatusDialog subscription={editingSub} onClose={() => setEditingSub(null)} onSuccess={onEditSuccess} />
            )}
            {viewingSub && (
                <NotificationDetailsDialog subscription={viewingSub} onClose={() => setViewingSub(null)} />
            )}
        </Modal>
    );
}
